<template>
    <nav class="flex-1 min-h-0 relative overflow-y-auto" aria-label="Conversations" scroll-region>
        <ul class="relative z-0 divide-y divide-gray-200" v-if="conversations.length">
            <li v-for="conversation in conversations" :key="conversation.other_user.id">
                <div
                    class="relative px-6 py-5 flex items-center space-x-3 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500"
                >
                    <div class="shrink-0">
                        <img
                            class="h-10 w-10 rounded-full"
                            :src="conversation.other_user.avatar_or_default"
                            :alt="conversation.other_user.full_name"
                        />
                    </div>
                    <div class="flex-1 min-w-0">
                        <inertia-link
                            @click.prevent="$emit('open-conversation-with', conversation.other_user.id)"
                            :href="
                                route('messages.show', {
                                    w: conversation.other_user.id,
                                })
                            "
                            class="focus:outline-none"
                            preserve-scroll
                        >
                            <!-- Extend touch target to entire panel -->
                            <span class="absolute inset-0" aria-hidden="true"></span>
                            <div class="flex justify-between items-center">
                                <p class="text-sm font-medium text-gray-900">
                                    {{ conversation.other_user.name_or_nickname }}
                                </p>
                                <div class="text-sm text-gray-400">
                                    {{ conversation.last_message.relative_date }}
                                </div>
                            </div>
                            <div class="flex justify-between items-center">
                                <p
                                    class="text-sm text-gray-500 truncate"
                                    v-html="conversation.last_message.message"
                                ></p>
                                <div class="flex">
                                    <div
                                        v-if="conversation.unread_messages_count"
                                        class="bg-green-400 flex shrink-0 h-6 w-6 mx-2 items-center justify-center rounded-full text-white"
                                    >
                                        {{ conversation.unread_messages_count }}
                                    </div>
                                    <!--                              <svg class="w-6 h-6 mx-2 text-indigo-400 shrink-0" v-show="conversation.pinned" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path></svg>-->
                                    <svg
                                        class="w-6 h-6 mx-2 text-indigo-400 shrink-0"
                                        v-show="conversation.is_pinned"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M3 3a1 1 0 000 2h11a1 1 0 100-2H3zM3 7a1 1 0 000 2h5a1 1 0 000-2H3zM3 11a1 1 0 100 2h4a1 1 0 100-2H3zM13 16a1 1 0 102 0v-5.586l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 101.414 1.414L13 10.414V16z"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </inertia-link>
                    </div>
                </div>
            </li>
        </ul>
        <div class="relative z-0" v-else>
            <div class="p-4">
                {{ $t('messages.no_conversations_yet') }}
            </div>

            <div class="w-full flex justify-center">
                <svg class="w-3/4" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M760.912 198.528C760.912 198.528 764.033 274.935 790 287.845L640.39 287.845L760.912 198.528Z"
                        fill="#C4B6FF"
                    ></path>
                    <path
                        d="M651.186 287.839L339.377 287.839C265.335 287.839 205.313 227.657 205.313 153.419V153.419C205.313 79.1815 265.335 19 339.377 19L629.212 19C703.254 19 763.277 79.1816 763.277 153.419V175.451C763.277 237.521 713.092 287.839 651.186 287.839Z"
                        fill="#C4B6FF"
                    ></path>
                    <path
                        d="M623.836 252.527C612.913 252.527 604.058 243.649 604.058 232.697C604.058 221.746 612.913 212.868 623.836 212.868C634.759 212.868 643.613 221.746 643.613 232.697C643.613 243.649 634.759 252.527 623.836 252.527Z"
                        fill="#B5CDFB"
                    ></path>
                    <path
                        d="M683.406 252.527C672.483 252.527 663.629 243.649 663.629 232.698C663.629 221.746 672.483 212.868 683.406 212.868C694.329 212.868 703.184 221.746 703.184 232.698C703.184 243.649 694.329 252.527 683.406 252.527Z"
                        fill="#B5CDFB"
                    ></path>
                    <path
                        d="M94.2362 580.664L387.08 580.664C440.771 580.664 484.296 537.139 484.296 483.448V483.448C484.296 429.757 440.771 386.232 387.08 386.232L125.63 386.232C71.9391 386.232 28.4141 429.757 28.4141 483.448L28.4141 514.842C28.4141 551.195 57.8836 580.664 94.2362 580.664Z"
                        fill="#7D55FF"
                    ></path>
                    <path
                        d="M94.3908 554.235C101.491 554.235 107.246 548.48 107.246 541.38C107.246 534.28 101.491 528.524 94.3908 528.524C87.2908 528.524 81.5352 534.28 81.5352 541.38C81.5352 548.48 87.2908 554.235 94.3908 554.235Z"
                        fill="#C4B6FF"
                    ></path>
                    <path
                        d="M133.113 554.235C140.213 554.235 145.969 548.48 145.969 541.38C145.969 534.28 140.213 528.524 133.113 528.524C126.013 528.524 120.258 534.28 120.258 541.38C120.258 548.48 126.013 554.235 133.113 554.235Z"
                        fill="#C4B6FF"
                    ></path>
                    <path
                        d="M28.9076 522.76C28.9076 522.76 26.8785 572.295 10 580.665L107.248 580.665L28.9076 522.76Z"
                        fill="#7D55FF"
                    ></path>
                </svg>
            </div>
        </div>
    </nav>
</template>
<script>
export default {
    name: 'conversation-list',
    props: {
        conversations: {
            type: Array,
            required: true,
        },
    },
};
</script>
